import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, X } from 'lucide-react';
import { adminApi } from '../../services/api';
import { Skeleton } from '../../components/ui/Skeleton';
import { CategoryIcon } from '../../components/ui/CategoryIcon';
import { generateSlug } from '../../utils/helpers';
import type { Category } from '../../types';

type CategoryForm = {
  name: string;
  slug: string;
  description: string;
  icon: string;
  parent_id: string;
  order: string;
};

const emptyForm: CategoryForm = { name: '', slug: '', description: '', icon: '', parent_id: '', order: '0' };

export const AdminCategories: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<'create' | 'edit' | null>(null);
  const [editing, setEditing] = useState<Category | null>(null);
  const [form, setForm] = useState<CategoryForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  const load = () => {
    setLoading(true);
    adminApi
      .categories()
      .then((res) => setCategories(res.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const topLevel = categories.filter((c) => !c.parent_id);
  const childrenOf = (id: number) => categories.filter((c) => c.parent_id === id);

  const openCreate = () => {
    setForm(emptyForm);
    setModal('create');
  };

  const openEdit = (category: Category) => {
    setEditing(category);
    setForm({
      name: category.name,
      slug: category.slug,
      description: category.description || '',
      icon: category.icon || '',
      parent_id: category.parent_id ? String(category.parent_id) : '',
      order: String(category.order ?? 0),
    });
    setModal('edit');
  };

  const closeModal = () => {
    setModal(null);
    setEditing(null);
  };

  const handleNameChange = (name: string) => {
    setForm((f) => ({ ...f, name, slug: modal === 'create' ? generateSlug(name) : f.slug }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (modal === 'create') {
        await adminApi.createCategory({
          name: form.name,
          slug: form.slug,
          description: form.description || undefined,
          icon: form.icon || undefined,
          parent_id: form.parent_id ? Number(form.parent_id) : undefined,
          order: Number(form.order) || 0,
        });
      } else if (modal === 'edit' && editing) {
        await adminApi.updateCategory(editing.id, {
          name: form.name,
          description: form.description || undefined,
          icon: form.icon || undefined,
          order: Number(form.order) || 0,
        });
      }
      closeModal();
      load();
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (category: Category) => {
    setTogglingId(category.id);
    try {
      await adminApi.updateCategory(category.id, { is_active: !category.is_active });
      setCategories((prev) => prev.map((c) => (c.id === category.id ? { ...c, is_active: !c.is_active } : c)));
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (category: Category) => {
    if (!window.confirm(`¿Eliminar la categoría "${category.name}"?`)) return;
    await adminApi.deleteCategory(category.id);
    load();
  };

  const renderRow = (category: Category, isChild = false) => (
    <tr key={category.id}>
      <td className={`px-5 py-3 ${isChild ? 'pl-12' : ''}`}>
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center shrink-0">
            <CategoryIcon name={category.icon} size={16} />
          </div>
          <span className="font-medium text-onSurface">{category.name}</span>
        </div>
      </td>
      <td className="px-5 py-3 text-onSurface-var">{category.slug}</td>
      <td className="px-5 py-3 text-onSurface-var">{category.jobs_count ?? 0}</td>
      <td className="px-5 py-3 text-onSurface-var">{category.order}</td>
      <td className="px-5 py-3">
        <button
          onClick={() => handleToggleActive(category)}
          disabled={togglingId === category.id}
          className={`badge ${category.is_active ? 'bg-success-100 text-success-700' : 'bg-secondary-100 text-secondary-600'} disabled:opacity-50`}
        >
          {category.is_active ? 'Activa' : 'Inactiva'}
        </button>
      </td>
      <td className="px-5 py-3">
        <div className="flex justify-end gap-1">
          <button
            onClick={() => openEdit(category)}
            className="p-2 rounded-lg text-onSurface-var hover:bg-surface-var transition-colors"
            title="Editar"
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleDelete(category)}
            className="p-2 rounded-lg text-danger-600 hover:bg-danger-50 transition-colors"
            title="Eliminar"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-onSurface">Categorías</h1>
          <p className="text-onSurface-mute">Organizá los oficios y rubros de la plataforma</p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors"
        >
          <Plus className="w-4 h-4" /> Nueva categoría
        </button>
      </div>

      <div className="bg-surface rounded-2xl border border-outlineVarWarm overflow-hidden">
        {loading ? (
          <div className="p-5 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12" />
            ))}
          </div>
        ) : topLevel.length > 0 ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-outlineVarWarm text-left text-onSurface-mute">
                <th className="px-5 py-3 font-medium">Nombre</th>
                <th className="px-5 py-3 font-medium">Slug</th>
                <th className="px-5 py-3 font-medium">Trabajos</th>
                <th className="px-5 py-3 font-medium">Orden</th>
                <th className="px-5 py-3 font-medium">Estado</th>
                <th className="px-5 py-3 font-medium text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outlineVarWarm">
              {topLevel.map((category) => (
                <React.Fragment key={category.id}>
                  {renderRow(category)}
                  {childrenOf(category.id).map((child) => renderRow(child, true))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-onSurface-mute text-center py-12">No hay categorías todavía</p>
        )}
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={closeModal}>
          <div
            className="bg-surface rounded-2xl border border-outlineVarWarm w-full max-w-md p-6 animate-fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-onSurface">
                {modal === 'create' ? 'Nueva categoría' : 'Editar categoría'}
              </h2>
              <button onClick={closeModal} className="p-1 rounded-lg text-onSurface-mute hover:bg-surface-var">
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="label">Nombre</label>
                <input
                  required
                  value={form.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  className="input"
                />
              </div>

              {modal === 'create' && (
                <div>
                  <label className="label">Slug</label>
                  <input
                    required
                    value={form.slug}
                    onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                    className="input"
                  />
                </div>
              )}

              <div>
                <label className="label">Descripción</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  className="input"
                  rows={2}
                />
              </div>

              <div>
                <label className="label">Ícono (lucide)</label>
                <input
                  value={form.icon}
                  onChange={(e) => setForm((f) => ({ ...f, icon: e.target.value }))}
                  placeholder="hammer, zap, droplet, paintbrush..."
                  className="input"
                />
              </div>

              {modal === 'create' && (
                <div>
                  <label className="label">Categoría padre</label>
                  <select
                    value={form.parent_id}
                    onChange={(e) => setForm((f) => ({ ...f, parent_id: e.target.value }))}
                    className="input"
                  >
                    <option value="">Ninguna (categoría principal)</option>
                    {topLevel.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="label">Orden</label>
                <input
                  type="number"
                  value={form.order}
                  onChange={(e) => setForm((f) => ({ ...f, order: e.target.value }))}
                  className="input"
                />
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-outlineVarWarm text-onSurface-var text-sm font-medium hover:bg-surface-var transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 disabled:opacity-50 transition-colors"
                >
                  {saving ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
